import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from 'react-redux';

import { CmsApiSearchLandingPage } from 'modules/cms/api/types/CmsApiSearchLandingPage';
import { searchTrackDisplayedListings } from 'modules/search/helpers/searchTrackDisplayedListings';
import { assignSearchPageUrlParametersToRedux } from 'modules/search/pages/Search/assignSearchPageUrlParametersToRedux';
import { SearchFacet } from 'modules/search/types/SearchFacet';
import { MainStoreState } from 'store/MainStoreState';
import { useMainDispatch } from 'store/hooks/useMainDispatch';
import { useUserEnvironment } from 'store/hooks/useUserEnvironment';
import { updateSearchLocation } from 'store/mainSearch/mainSearch.actions';
import { mainStoreFetchPage } from 'store/mainSearch/mainStoreFetchPage';
import { EMPTY_SEARCH_LOCATION } from 'utils/constants/general/emptySearchLocation';

type Args = {
  content: CmsApiSearchLandingPage;
  isSsr: boolean;
  getSearchTrackingData: () => Record<string, unknown>;
  searchFacets: SearchFacet[];
};

export function useSearchLandingPageLifecycle({
  content,
  isSsr,
  getSearchTrackingData,
  searchFacets,
}: Args) {
  const dispatch = useMainDispatch();
  const { isEnvironmentLoaded } = useUserEnvironment();

  const [initialized, setInitialized] = useState(isSsr);

  const getState = useStore().getState as () => MainStoreState;
  const initialize = useCallback(async () => {
    assignSearchPageUrlParametersToRedux({
      dispatch,
      getState,
      searchParams: new URLSearchParams(
        queryString.stringify({
          ...content.filters,
          radius: content.radius,
          q: content.searchText,
        }),
      ),
      searchType: content.filters.type,
      searchFacets,
    });

    dispatch(
      updateSearchLocation(
        content.location?.location || EMPTY_SEARCH_LOCATION,
        false,
      ),
    );

    const results = await dispatch(mainStoreFetchPage());
    searchTrackDisplayedListings(results.hits, getSearchTrackingData());

    setInitialized(true);
  }, [
    content.filters,
    content.location,
    content.radius,
    content.searchText,
    dispatch,
    getSearchTrackingData,
    getState,
    searchFacets,
  ]);

  useEffect(() => {
    // Usually the user "lands" on the landing page, which has been initialized
    // through or backend.
    if (!isEnvironmentLoaded || initialized) return;

    // But in the case where the user goes to a different page and comes back, we
    // still need to initialize it
    initialize();
  }, [dispatch, isEnvironmentLoaded, initialize, initialized]);

  return { initialized };
}
